import {TaskSetFormState} from './useTaskSetPreview';
import {logTrackingError, trackEvent} from '../../../../../tracking/amplitude/TrackingV2';

/**
 * Sends a task set usage event. The properties are built inside the guard, so
 * a mistake in working them out is logged like any other tracking failure
 * instead of escaping into the save callback that called this -- which would
 * leave a saved set's form open, as if the save had failed.
 */
export const trackTaskSetEvent = (action: string, buildProperties: () => Record<string, unknown> = () => ({})): void => {
	try {
		trackEvent('Task Set', action, buildProperties());
	} catch (e) {
		logTrackingError(e as Error);
	}
};

/** Where a single instance's rate comes from on its own date. */
export type RateSourceOnDate = 'role' | 'default_role' | 'none';

/**
 * Where a set's instances are priced from. 'mixed' when instances on different
 * dates resolve to different sources, which a versioned rate card allows.
 */
export type RateSource = RateSourceOnDate | 'mixed';

/**
 * Where the set's pricing comes from across all of its dates.
 *
 * Null when the host cannot answer -- a host with no rate card passes no lookup,
 * and saying 'none' there would claim the card prices nothing when nobody looked.
 * A points-estimated project reports 'none'.
 */
export const summariseRateSource = (
	dates: ReadonlyArray<string>,
	roleId: string | null,
	isPointsProject: boolean,
	rateSourceOnDate?: (roleId: string | null, date: string) => RateSourceOnDate
): RateSource | null => {
	// First: a points project is priced by no card, which needs no card to know.
	if (isPointsProject) {
		return 'none';
	}
	if (!rateSourceOnDate) {
		return null;
	}
	const sources = new Set(dates.map(date => rateSourceOnDate(roleId, date)));
	if (sources.size === 0) {
		return 'none';
	}
	if (sources.size > 1) {
		return 'mixed';
	}
	return sources.values().next().value as RateSourceOnDate;
};

/**
 * The repeat pattern as one value: the frequency, and for a monthly set which
 * kind of monthly day it falls on.
 */
export const describePatternForTracking = (form: Pick<TaskSetFormState, 'frequency' | 'monthlyMode'>): string =>
	form.frequency === 'MONTHLY' && form.monthlyMode ? `${form.frequency}_${form.monthlyMode}` : form.frequency;

/**
 * The order role and assignees were first picked in one visit to the create
 * form. Only the first pick of each field is kept: what is being measured is
 * which one people reach for first, not how often they change their minds.
 */
export interface CreateSelectionOrder {
	firstRoleAt: number | null;
	firstAssigneeAt: number | null;
	/** The first person picked, whether or not they are still assigned. */
	firstAssigneeId: string | null;
}

export const EMPTY_SELECTION_ORDER: CreateSelectionOrder = {
	firstRoleAt: null,
	firstAssigneeAt: null,
	firstAssigneeId: null,
};

/** Records the first time a role is picked. Clearing the role is not a pick. */
export const noteRolePicked = (order: CreateSelectionOrder, roleId: string | null, now: number): CreateSelectionOrder =>
	roleId && order.firstRoleAt === null ? {...order, firstRoleAt: now} : order;

/**
 * Records the first person picked. The assignee control reports the whole list
 * each time, so the pick is whoever is in the new list and not the old one.
 */
export const noteAssigneesPicked = (
	order: CreateSelectionOrder,
	previousIds: ReadonlyArray<string>,
	nextIds: ReadonlyArray<string>,
	now: number
): CreateSelectionOrder => {
	if (order.firstAssigneeId !== null) {
		return order;
	}
	const added = nextIds.find(id => !previousIds.includes(id));
	return added ? {...order, firstAssigneeAt: now, firstAssigneeId: added} : order;
};

export type FirstFieldSelected = 'role' | 'assignee' | 'none';

export const firstFieldSelected = (order: CreateSelectionOrder): FirstFieldSelected => {
	if (order.firstRoleAt === null && order.firstAssigneeAt === null) {
		return 'none';
	}
	if (order.firstRoleAt === null) {
		return 'assignee';
	}
	if (order.firstAssigneeAt === null) {
		return 'role';
	}
	return order.firstAssigneeAt < order.firstRoleAt ? 'assignee' : 'role';
};

/**
 * Milliseconds from the first assignee pick to the first role pick. Null unless
 * an assignee was picked first and a role after it.
 */
export const msFirstAssigneeToRole = (order: CreateSelectionOrder): number | null =>
	firstFieldSelected(order) === 'assignee' && order.firstRoleAt !== null && order.firstAssigneeAt !== null
		? order.firstRoleAt - order.firstAssigneeAt
		: null;

/**
 * Each project member's role on the project, by person id, for
 * chosenRoleMatchesFirstAssigneeRole. A member with no person behind it has
 * nothing to key on and is left out.
 */
export const projectRolesByPersonId = (
	members: ReadonlyArray<{person?: {id?: string | null} | null; role?: {id?: string | null} | null}>
): Record<string, string | null> =>
	Object.fromEntries(members.flatMap(member => (member.person?.id ? [[member.person.id, member.role?.id ?? null]] : [])));

/**
 * Whether the set's role is the first-picked person's role on this project.
 *
 * Their role on the PROJECT, not their company-level role: a person has one role
 * per project and it can differ from both. Null when the set has no role or no
 * assignees, or when that person has no role on the project -- which happens,
 * since a project role is optional and the timeline lets you assign people not
 * yet on the project.
 */
export const chosenRoleMatchesFirstAssigneeRole = (
	order: CreateSelectionOrder,
	roleId: string | null,
	assigneeCount: number,
	projectRoleByPersonId?: Readonly<Record<string, string | null | undefined>>
): boolean | null => {
	if (!roleId || assigneeCount === 0 || !order.firstAssigneeId || !projectRoleByPersonId) {
		return null;
	}
	const projectRole = projectRoleByPersonId[order.firstAssigneeId];
	return projectRole ? projectRole === roleId : null;
};

export type EditScope = 'all_eligible' | 'from_instance' | 'not_asked';

/**
 * How far a saved edit was told to reach. Null when no field that reaches
 * instances changed, so there was nothing to scope. 'not_asked' when there was,
 * but the chooser was skipped because the server found the set untouched: every
 * instance took the change without the reader choosing it.
 */
export const editScope = (reachesInstances: boolean, scopeAsked: boolean, fromSequence: number | null): EditScope | null => {
	if (!reachesInstances) {
		return null;
	}
	if (!scopeAsked) {
		return 'not_asked';
	}
	return fromSequence === null ? 'all_eligible' : 'from_instance';
};

export type EndsChange = 'extended' | 'shortened' | 'unchanged';

/**
 * Which way a saved Ends moved, measured against the end the field opened on:
 * the stored end of a date-bounded set, or the last date generated for a count-
 * bounded or short one. Not against the last instance -- a date-bounded set's
 * end can sit past it, so an end moved earlier but still after that instance
 * would read as an extension. 'unchanged' when the end lands where it was,
 * which is how a set that came up short is accepted as it stands. Null when
 * Ends was not part of the save, or there was no end to measure from.
 */
export const endsChange = (endsChanged: boolean, newEnd: string, previousEnd: string | null): EndsChange | null => {
	if (!endsChanged || !previousEnd) {
		return null;
	}
	if (newEnd > previousEnd) {
		return 'extended';
	}
	return newEnd < previousEnd ? 'shortened' : 'unchanged';
};
